// Pure unit-conversion helpers for the "Store Canonical, Convert at Boundaries" pattern.
// Internal canonical units: inches (length), grams (weight).
// These functions convert between canonical storage and user-facing display values.

export const MM_PER_INCH = 25.4;
export const CM_PER_INCH = 2.54;
export const GRAMS_PER_OZ = 28.3495;

// ── Length ────────────────────────────────────────────────────────────
/** Canonical inches → display value (inches or mm) */
export const lengthToDisplay = (inches, isMetric) => {
  const num = parseFloat(inches);
  if (isNaN(num) || inches === '' || inches === null || inches === undefined) return '';
  return isMetric ? Math.round(num * MM_PER_INCH * 100) / 100 : num;
};

/** Display value (inches or mm) → canonical inches */
export const lengthFromDisplay = (displayValue, isMetric) => {
  const num = parseFloat(displayValue);
  if (isNaN(num) || displayValue === '' || displayValue === null || displayValue === undefined) return '';
  return isMetric ? Math.round((num / MM_PER_INCH) * 10000) / 10000 : num;
};

// ── Weight ────────────────────────────────────────────────────────────
/** Canonical grams → display value (grams or oz) */
export const weightToDisplay = (grams, isMetricWeight) => {
  const num = parseFloat(grams);
  if (isNaN(num) || grams === '' || grams === null || grams === undefined) return '';
  return isMetricWeight ? num : Math.round((num / GRAMS_PER_OZ) * 100) / 100;
};

/** Display value (grams or oz) → canonical grams */
export const weightFromDisplay = (displayValue, isMetricWeight) => {
  const num = parseFloat(displayValue);
  if (isNaN(num) || displayValue === '' || displayValue === null || displayValue === undefined) return '';
  return isMetricWeight ? num : Math.round(num * GRAMS_PER_OZ * 100) / 100;
};

// ── Area ──────────────────────────────────────────────────────────────
/** Canonical square inches → display value (in² or cm²) */
export const areaToDisplay = (sqInches, isMetric) => {
  const num = parseFloat(sqInches);
  if (isNaN(num)) return 0;
  return isMetric ? Math.round(num * CM_PER_INCH * CM_PER_INCH * 10000) / 10000 : num;
};

/** Display value (in² or cm²) → canonical square inches */
export const areaFromDisplay = (displayValue, isMetric) => {
  const num = parseFloat(displayValue);
  if (isNaN(num)) return 0;
  return isMetric ? num / (CM_PER_INCH * CM_PER_INCH) : num;
};

// ── Density ───────────────────────────────────────────────────────────
/** Canonical g/in² → display value in user's preferred weight/area units */
export const densityToDisplay = (gramsPerSqInch, isMetric, isMetricWeight) => {
  const num = parseFloat(gramsPerSqInch);
  if (isNaN(num)) return 0;
  // Convert weight: g → oz if needed
  const weightPart = isMetricWeight ? num : num / GRAMS_PER_OZ;
  // Convert area: per in² → per cm² if needed (divide by cm² per in²)
  const densityVal = isMetric ? weightPart / (CM_PER_INCH * CM_PER_INCH) : weightPart;
  return Math.round(densityVal * 10000) / 10000;
};

// ── Labels ────────────────────────────────────────────────────────────
export const lengthUnitLabel = (isMetric) => isMetric ? 'mm' : 'in';
export const weightUnitLabel = (isMetricWeight) => isMetricWeight ? 'g' : 'oz';
export const areaUnitLabel = (isMetric) => isMetric ? 'cm²' : 'in²';
export const densityUnitLabel = (isMetric, isMetricWeight) => `${weightUnitLabel(isMetricWeight)}/${areaUnitLabel(isMetric)}`;
